/*eslint no-prototype-builtins: "off"*/
import UncountableRule from './helpers/uncountable.rules'
import SingularizationRules from './helpers/singularization.rules'
import PluralizationRules from './helpers/pluralization.rules'
import IrregularRule from './helpers/irregular.rules'

type Map = { [key: string]: string }

export default class Pluralizer {
  private pluralRules = []
  private singularRules = []
  private uncountables = {}
  private irregularPlurals = {}
  private irregularSingles = {}

  get isSingular() {
    return this.checkWord(
      this.irregularPlurals,
      this.irregularSingles,
      this.singularRules
    )
  }

  constructor() {
    UncountableRule(this.addUncountableRule)
    SingularizationRules(this.addSingularRule)
    PluralizationRules(this.addPluralRule)
    IrregularRule(this.addIrregularRule)
  }

  private sanitizeRule = (rule: RegExp | string): RegExp => {
    if (typeof rule === 'string') {
      return new RegExp('^' + rule + '$', 'i')
    }
    return rule
  }

  static restoreCase(word: string, token: string) {
    return token
  }

  static interpolate(str: string, args: string[]): string {
    return str.replace(/\$(\d{1,2})/g, (match, index) => args[index] || '')
  }

  private replace = (word: string, rule: RegExp): string =>
    word.replace(rule[0], (match, index) => {
      const result = Pluralizer.interpolate(rule[1], [match, index, word])
      if (match === '') {
        return Pluralizer.restoreCase(word[index - 1], result)
      }
      return Pluralizer.restoreCase(match, result)
    })

  private sanitizeWord = (
    token: string,
    word: string,
    rules: RegExp[]
  ): string => {
    if (!token.length || this.uncountables.hasOwnProperty(token)) {
      return word
    }
    let len = rules.length
    while (len--) {
      const rule = rules[len]
      if (rule[0].test(word)) return this.replace(word, rule)
    }
    return word
  }

  private replaceWord =
    (replaceMap: Map, keepMap: Map, rules: RegExp[]) => word => {
      const token = word.toLowerCase()
      if (keepMap.hasOwnProperty(token)) {
        return Pluralizer.restoreCase(word, token)
      }
      if (replaceMap.hasOwnProperty(token)) {
        return Pluralizer.restoreCase(word, replaceMap[token])
      }
      return this.sanitizeWord(token, word, rules)
    }

  private checkWord = (replaceMap, keepMap, rules) => word => {
    const token = word.toLowerCase()
    if (keepMap.hasOwnProperty(token)) return true
    if (replaceMap.hasOwnProperty(token)) return false
    return this.sanitizeWord(token, token, rules) === token
  }

  public pluralize = (
    word: string,
    count?: number,
    inclusive?: boolean
  ): string => {
    const pluralized =
      count === 1
        ? this.replaceWord(
            this.irregularPlurals,
            this.irregularSingles,
            this.singularRules
          )(word)
        : this.replaceWord(
            this.irregularSingles,
            this.irregularPlurals,
            this.pluralRules
          )(word)

    return (inclusive ? count + ' ' : '') + pluralized
  }

  public addPluralRule = (rule: string | RegExp, replacement: string) => {
    this.pluralRules.push([this.sanitizeRule(rule), replacement])
  }

  public addSingularRule = (rule: string | RegExp, replacement: string) => {
    this.singularRules.push([this.sanitizeRule(rule), replacement])
  }

  public addUncountableRule = (word: string | RegExp) => {
    if (typeof word === 'string') {
      this.uncountables[word.toLowerCase()] = true
      return
    }
    this.addPluralRule(word, '$0')
    this.addSingularRule(word, '$0')
  }

  public addIrregularRule = (single: string, plural: string) => {
    plural = plural.toLowerCase()
    single = single.toLowerCase()
    this.irregularSingles[single] = plural
    this.irregularPlurals[plural] = single
  }
}
